import { ehErroHttp } from '@/shared/utils/erroHttp';

import type { AcaoErroCriacaoConta, ErroCriacaoConta, ErroCriacaoContaCodigo } from '../types';

export const MENSAGEM_FALHA_CRIACAO_CONTA =
  'Não conseguimos criar sua conta agora. Tente novamente.';
export const ERRO_CRIACAO_DESCONHECIDO: ErroCriacaoConta = {
  codigo: 'DESCONHECIDO',
  mensagem: MENSAGEM_FALHA_CRIACAO_CONTA,
};
const MENSAGEM_MUITAS_TENTATIVAS =
  'Muitas tentativas seguidas. Aguarde alguns minutos e tente de novo.';

const CODIGOS_DO_SERVIDOR: ReadonlySet<string> = new Set<ErroCriacaoContaCodigo>([
  'CADASTRO_SELF_INDISPONIVEL',
  'EMAIL_JA_POSSUI_CONTA',
  'EMPRESA_JA_CADASTRADA',
  'PAGAMENTO_EM_ANDAMENTO',
  'EMAIL_EM_OUTRO_CADASTRO',
  'CADASTRO_EM_ANDAMENTO',
  'CNPJ_NAO_ENCONTRADO',
  'RECEITA_INDISPONIVEL',
  'PRESENTE_INDISPONIVEL',
]);

// Os 5xx nunca mostram o texto do servidor (pode carregar detalhe interno); os
// 4xx sem entrada aqui usam a mensagem que o core-api já escreve para a pessoa.
const MENSAGEM_FIXA: Partial<Record<ErroCriacaoContaCodigo, string>> = {
  CADASTRO_SELF_INDISPONIVEL: 'Cadastro indisponível no momento. Fale com quem te enviou o link.',
  CADASTRO_EM_ANDAMENTO: 'Estamos finalizando sua conta. Tente de novo em instantes.',
  RECEITA_INDISPONIVEL: 'Não conseguimos consultar a Receita agora. Tente novamente em instantes.',
  PRESENTE_INDISPONIVEL:
    'Não conseguimos liberar seu saldo inicial agora. Tente novamente em instantes.',
};

const ehCodigoDoServidor = (codigo: string | null): codigo is ErroCriacaoContaCodigo =>
  codigo !== null && CODIGOS_DO_SERVIDOR.has(codigo);

/**
 * Classifica a falha de `POST /onboarding-self/contas` por status e `error.code`,
 * nunca pelo texto. O 429 das cotas não traz `code` (corpo `{ error, statusCode }`),
 * por isso é lido pelo status.
 */
export const classificarErroCriacaoConta = (erro: unknown): ErroCriacaoConta => {
  if (!ehErroHttp(erro)) return ERRO_CRIACAO_DESCONHECIDO;
  if (erro.status === 429) {
    return { codigo: 'MUITAS_TENTATIVAS', mensagem: MENSAGEM_MUITAS_TENTATIVAS };
  }
  if (!ehCodigoDoServidor(erro.codigo)) return ERRO_CRIACAO_DESCONHECIDO;
  const mensagem =
    MENSAGEM_FIXA[erro.codigo] ?? (erro.status < 500 ? erro.message : MENSAGEM_FALHA_CRIACAO_CONTA);
  return { codigo: erro.codigo, mensagem };
};

const CODIGOS_PARA_LOGIN: ReadonlySet<ErroCriacaoContaCodigo> = new Set([
  'EMPRESA_JA_CADASTRADA',
  'EMAIL_JA_POSSUI_CONTA',
]);

// Repetir a mesma requisição daria o mesmo desfecho: o caminho é revisar os
// dados, falar com o suporte ou com quem enviou o link.
const CODIGOS_SEM_NOVA_TENTATIVA: ReadonlySet<ErroCriacaoContaCodigo> = new Set([
  'CADASTRO_SELF_INDISPONIVEL',
  'PAGAMENTO_EM_ANDAMENTO',
  'EMAIL_EM_OUTRO_CADASTRO',
  'CNPJ_NAO_ENCONTRADO',
]);

export const acaoDoErroCriacaoConta = (codigo: ErroCriacaoContaCodigo): AcaoErroCriacaoConta => {
  if (CODIGOS_PARA_LOGIN.has(codigo)) return 'acessarConta';
  if (CODIGOS_SEM_NOVA_TENTATIVA.has(codigo)) return 'nenhuma';
  return 'tentarNovamente';
};
